import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function BillingReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error | cancelled
  const [info, setInfo] = useState({});
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  const home = user?.role === "client" ? "/client/billing" : user?.role === "recruiter_partner" ? "/partner/billing" : "/candidate";

  const poll = useCallback(async (attempt = 0) => {
    if (attempt >= 6) { setState("error"); return; }
    try {
      const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") { setInfo(data); setState("success"); return; }
      if (data.status === "expired") { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch { setState("error"); }
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) { setState("cancelled"); return; }
    if (!sessionId) { setState("error"); return; }
    poll();
  }, [sessionId, cancelled, poll]);

  const MAP = {
    checking: { icon: Loader2, spin: true, title: "Confirming your payment…", body: "This only takes a moment.", color: "#002EA6" },
    success: { icon: CheckCircle2, title: "Payment successful", body: "Your purchase is confirmed. You're all set.", color: "#10B981" },
    cancelled: { icon: XCircle, title: "Checkout cancelled", body: "No charge was made. You can try again anytime.", color: "#F5A623" },
    error: { icon: XCircle, title: "We couldn't confirm payment", body: "If you were charged, it will reflect shortly. Contact us if this persists.", color: "#e11" },
  };
  const s = MAP[state];
  const isJob = state === "success" && info.kind === "job_posting";
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full border border-black/10 bg-white p-10 text-center">
        <s.icon className={`h-12 w-12 mx-auto ${s.spin ? "animate-spin" : ""}`} style={{ color: s.color }} strokeWidth={1.5} />
        <h1 className="font-heading font-bold text-2xl mt-6">{isJob ? "Your posting is live" : s.title}</h1>
        <p className="text-sm text-muted-foreground mt-2">{isJob ? "Payment confirmed — your role is now published and visible to candidates." : s.body}</p>
        {state !== "checking" && (
          <div className="mt-8 flex flex-col items-center gap-3">
            {isJob && (
              <button onClick={() => navigate("/featured-jobs")} data-testid="view-live-listing" className="px-6 py-2.5 text-sm bg-primary text-white hover:bg-[#09090B] transition-colors w-full">
                View your live listing
              </button>
            )}
            <button onClick={() => navigate(isJob ? (user?.role === "recruiter_partner" ? "/partner" : "/client/jobs") : home)} className={`px-6 py-2.5 text-sm transition-colors w-full ${isJob ? "border border-black/15 hover:bg-black/5" : "bg-primary text-white hover:bg-[#09090B]"}`}>
              {isJob ? "Manage your postings" : `Back to ${user?.role === "client" ? "billing" : "dashboard"}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
